import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Validators, FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { WebService}  from '../services/web/web.service';
import { ToastService } from '../services/notification/toast.service';
import {SpinnerService} from '../services/spinner/spinner.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { HttpParams} from '@angular/common/http';
import { GoogleSignInService } from '../services/auth/google-signin.service';
import { LoggerService } from '../services/logger.service';
import { AuthType } from '../models/auth.models';
import { SecurityService } from '../services/security.service';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-create-account',
    templateUrl: './create-account.component.html',
    styleUrls: ['./create-account.component.scss'],
    standalone: false
})
export class CreateAccountComponent implements OnInit {

    signUpForm: FormGroup;
    googleClientId = environment.googleClientId;

    constructor(
        private router : Router,
        private spinner : SpinnerService,
        private webService : WebService,
        private toast : ToastService,
        private auth : AuthService,
        private googleSignInService: GoogleSignInService,
        private logger: LoggerService,
        private securityService: SecurityService,
        private translate: TranslateService
    ) {
        this.signUpForm = new FormGroup({
            email: new FormControl('', [Validators.required,Validators.email])
        });
    }

    ngOnInit(): void {
        this.logger.log('🔵 [REGISTRO] Componente de registro inicializado');
        // Escuchar autenticación de Google
        this.subscribeToGoogleAuth();
    }

    /**
     * Registro tradicional con email (sin Google)
     */
    singUp() {
        this.logger.log('🟢 [REGISTRO-STANDARD] Iniciando registro estándar');

        if (this.signUpForm.valid) {
            this.spinner.open();
            const signinData = this.signUpForm.value;

            this.logger.log('📧 [REGISTRO-STANDARD] Email:', signinData.email);
            this.logger.log('📤 [REGISTRO-STANDARD] Enviando a:', this.webService.HOST + "/billcentrix/V2/user");

            this.webService.post(signinData, this.webService.HOST + "/billcentrix/V2/user").subscribe(res => {
                this.logger.log('✅ [REGISTRO-STANDARD] Registro exitoso');
                this.spinner.close();
                this.toast.showSuccess(this.translate.instant('createAccount.messages.success'));
                this.router.navigateByUrl("/login");
            }, err => {
                this.logger.error('❌ [REGISTRO-STANDARD] Error en registro:', err);
                this.spinner.close();
                console.log(err);
                this.toast.showError(err);
            });
        } else {
            this.logger.warn('⚠️ [REGISTRO-STANDARD] Formulario inválido');
            this.toast.showError(this.translate.instant('createAccount.messages.invalidEmail'));
        }
    }

    /**
     * Suscribe al estado de autenticación de Google
     */
    private subscribeToGoogleAuth(): void {
        this.logger.log('🔵 [REGISTRO] Suscribiéndose al estado de autenticación de Google');

        this.googleSignInService.getAuthState().subscribe({
            next: (googleUser) => {
                if (googleUser) {
                    this.logger.log('✅ [REGISTRO] Usuario autenticado con Google:', googleUser.email);
                    this.handleGoogleRegister(googleUser);
                }
            },
            error: (error) => {
                this.logger.error('❌ [REGISTRO] Error en el estado de autenticación:', error);
                this.toast.showError(this.translate.instant('createAccount.messages.googleError'));
            }
        });
    }

    /**
     * Maneja el registro con Google
     */
    private handleGoogleRegister(googleUser: any): void {
        this.logger.log('🟢 [REGISTRO-GOOGLE] ========================================');
        this.logger.log('🟢 [REGISTRO-GOOGLE] Iniciando proceso de registro con GOOGLE');
        this.spinner.open();

        // Validar datos
        if (!googleUser.email || !googleUser.idToken) {
            this.logger.error('❌ [REGISTRO-GOOGLE] Datos incompletos');
            this.spinner.close();
            this.toast.showError(this.translate.instant('login.messages.googleDataIncomplete'));
            return;
        }

        // Crear payload para el backend
        const payload = {
            email: googleUser.email,
            authType: AuthType.GOOGLE,
            googleToken: googleUser.idToken,
            name: googleUser.name,
            photoUrl: googleUser.photoUrl
        };

        this.logger.log('📤 [REGISTRO-GOOGLE] Enviando al backend:', this.webService.HOST + "/auth/register/google");

        // Llamar al nuevo endpoint de registro con Google
        this.webService.post(payload, this.webService.HOST + "/auth/register/google").subscribe({
            next: (response: any) => {
                this.logger.log('✅ [REGISTRO-GOOGLE] Registro exitoso');
                this.spinner.close();

                if (response.jwt) {
                    this.securityService.storeSession(response);
                } else {
                    this.logger.warn('⚠️ [REGISTRO-GOOGLE] No se encontró JWT en la respuesta');
                }

                this.toast.showSuccess(this.translate.instant('createAccount.messages.googleSuccess'));

                // Redirigir a /validate
                this.logger.log('🚀 [REGISTRO-GOOGLE] Redirigiendo a /validate');
                this.router.navigate(['/validate']);
            },
            error: (error) => {
                this.logger.error('❌ [REGISTRO-GOOGLE] Error al registrar');
                this.logger.error('❌ [REGISTRO-GOOGLE] Detalles:', error);
                this.spinner.close();

                // Manejar diferentes tipos de errores
                this.handleRegistrationError(error);
            }
        });
    }

    /**
     * Maneja los errores de registro
     */
    private handleRegistrationError(error: any): void {
        if (error.status === 409) {
            // Usuario ya existe
            this.logger.warn('⚠️ [REGISTRO-GOOGLE] Usuario ya existe');
            this.toast.showError(this.translate.instant('createAccount.messages.emailExists'));

            setTimeout(() => {
                this.logger.log('🔄 [REGISTRO-GOOGLE] Redirigiendo al login...');
                this.router.navigate(['/login']);
            }, 2000);

        } else if (error.status === 400) {
            // Token inválido o email no verificado
            this.logger.error('❌ [REGISTRO-GOOGLE] Token inválido');
            this.toast.showError(this.translate.instant('createAccount.messages.googleAuthError'));

        } else if (error.status === 500) {
            // Error del servidor
            this.logger.error('❌ [REGISTRO-GOOGLE] Error del servidor');
            this.toast.showError(this.translate.instant('common.errors.serverError'));

        } else {
            // Error genérico
            this.logger.error('❌ [REGISTRO-GOOGLE] Error desconocido');
            const errorMessage = error.error?.message || error.message || this.translate.instant('createAccount.messages.genericError');
            this.toast.showError(errorMessage);
        }
    }
    onLoginClick() {
        this.router.navigate(["/login"])

    }
    goBack(){
        this.router.navigate(['']);
    }
    goHome() {
        this.router.navigate(['/']);
    }
}
